import DownloadIcon from '@mui/icons-material/Download';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AcceptTerms from './AcceptTerms';
import { useAuth } from './AuthContext';
import './App.css';
import Footer from './Footer';
import LawAppBar from './LawAppBar';
import LoginScreen from './LoginScreen';
import {
    handleGoogleLoginSuccess,
    renderGoogleLoginButton,
} from './LoginUtils';

export default function ExecuteBaa() {
    const {
        isAuthenticated,
        isLoading: authLoading,
        setAuthenticatedUser,
        logout,
    } = useAuth();
    const [termsStatus, setTermsStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    function onGoogleLoginSuccess(response) {
        handleGoogleLoginSuccess(response, onLoginComplete).catch(handleLoginError);
    }

    function onEmailLoginSuccess(user) {
        onLoginComplete(user);
    }

    function onLoginComplete(user) {
        if (user) {
            setAuthenticatedUser(user);
        }
        fetchTermsStatus();
    }

    function handleLoginError(err) {
        console.error('Login failed:', err);
        doLogout();
    }

    function renderLoginButton() {
        renderGoogleLoginButton("googleLogin", onGoogleLoginSuccess);
    }

    function doLogout() {
        logout(renderLoginButton);
    }

    function fetchTermsStatus() {
        setIsLoading(true);
        setError(null);
        fetch('/api/terms-status', {
            method: 'GET',
            credentials: 'same-origin'
        })
            .then(res => {
                if (res.status === 401) {
                    doLogout();
                    return null;
                }
                if (!res.ok) {
                    throw new Error('Failed to load terms status');
                }
                return res.json();
            })
            .then(data => {
                if (data) {
                    setTermsStatus(data);
                }
                setIsLoading(false);
            })
            .catch(err => {
                console.error('Error fetching terms status:', err);
                setError(err.message || 'An error occurred. Please try again.');
                setIsLoading(false);
            });
    }

    function handleTermsAccepted() {
        fetchTermsStatus();
    }

    useEffect(() => {
        if (authLoading) {
            return;
        }
        if (isAuthenticated) {
            fetchTermsStatus();
        } else {
            setIsLoading(false);
        }
    }, [authLoading, isAuthenticated]);

    if (authLoading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                background: 'var(--background-color)',
            }}>
                <img src="/loading.gif" alt="loading..." style={{ height: '48px' }} />
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <LoginScreen
                onGoogleLoginSuccess={onGoogleLoginSuccess}
                onEmailLoginSuccess={onEmailLoginSuccess}
            />
        );
    }

    if (termsStatus?.status === 'acceptance-required') {
        return (
            <AcceptTerms
                termsType={termsStatus.reason || 'new-user'}
                onTermsAccepted={handleTermsAccepted}
            />
        );
    }

    return (
        <div className="App">
            <LawAppBar />
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 'calc(100vh - 48px)'
            }}>
                <Container
                    maxWidth="md"
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: { xs: '40px 20px', sm: '60px 40px' },
                        marginTop: '40px',
                        marginBottom: '60px',
                        textAlign: 'center'
                    }}
                >
                    {isLoading ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '200px'
                        }}>
                            <img
                                src="/loading.gif"
                                alt="Loading..."
                                style={{
                                    height: "50px",
                                    filter: 'opacity(0.8)'
                                }}
                            />
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'var(--text-secondary)',
                                    mt: 2
                                }}
                            >
                                Checking terms status...
                            </Typography>
                        </Box>
                    ) : error ? (
                        <Paper
                            elevation={0}
                            sx={{
                                padding: 4,
                                borderRadius: '12px',
                                border: '1px solid var(--border-color)',
                                backgroundColor: 'var(--surface-color)',
                                maxWidth: '500px',
                                width: '100%'
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'var(--text-primary)',
                                    mb: 2
                                }}
                            >
                                {error}
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={fetchTermsStatus}
                                sx={{
                                    background: 'var(--button-gradient)',
                                    color: 'white',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: '12px',
                                    px: 3,
                                    py: 1.5,
                                    '&:hover': {
                                        background: 'var(--button-gradient-hover)'
                                    }
                                }}
                            >
                                Retry
                            </Button>
                        </Paper>
                    ) : termsStatus?.status === 'current' ? (
                        <Paper
                            elevation={0}
                            sx={{
                                padding: { xs: 3, sm: 5 },
                                borderRadius: '12px',
                                boxShadow: 'var(--shadow-light)',
                                border: '1px solid var(--border-color)',
                                backgroundColor: 'var(--surface-color)',
                                maxWidth: '560px',
                                width: '100%'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 64,
                                    height: 64,
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(76, 175, 80, 0.12)',
                                    margin: '0 auto 24px'
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '2.5rem',
                                        color: 'var(--success-color, #4CAF50)',
                                        lineHeight: 1
                                    }}
                                >
                                    ✓
                                </Typography>
                            </Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontWeight: 600,
                                    mb: 2,
                                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                }}
                            >
                                BAA Successfully Executed
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontSize: '1rem',
                                    lineHeight: 1.7,
                                    mb: 4
                                }}
                            >
                                You have successfully executed a BAA with AttorneyAide. You can download it for your records.
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                <Button
                                    component="a"
                                    href="/BusinessAssociateAgreement(AttorneyAide).pdf"
                                    download="BusinessAssociateAgreement(AttorneyAide).pdf"
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    sx={{
                                        background: 'var(--button-gradient)',
                                        color: 'white',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: '12px',
                                        px: 4,
                                        py: 1.5,
                                        boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                                        textDecoration: 'none',
                                        '&:hover': {
                                            background: 'var(--button-gradient-hover)',
                                            boxShadow: '0 8px 25px rgba(212, 175, 55, 0.5)'
                                        }
                                    }}
                                >
                                    Download BAA
                                </Button>
                                <Button
                                    component={Link}
                                    to="/mycases"
                                    variant="outlined"
                                    startIcon={<FolderOpenIcon />}
                                    sx={{
                                        color: 'var(--text-primary)',
                                        borderColor: 'var(--border-color)',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: '12px',
                                        px: 4,
                                        py: 1.5,
                                        textDecoration: 'none',
                                        '&:hover': {
                                            borderColor: 'var(--text-secondary)',
                                            backgroundColor: 'var(--surface-color)'
                                        }
                                    }}
                                >
                                    Go to my cases
                                </Button>
                            </Box>
                        </Paper>
                    ) : null}
                </Container>
                <Footer />
            </Box>
        </div>
    );
}
